<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading && !detail" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
      <div class="text-muted mt-3">Loading submission details...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="storeError && !detail" class="alert alert-danger">
      <i class="fa fa-exclamation-circle mr-1"></i> {{ storeError }}
      <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadDetail">Retry</b-button>
    </div>

    <template v-else-if="detail">
      <!-- Top Bar -->
      <div class="card mb-3">
        <div class="body py-3">
          <div class="row align-items-center">
            <div class="col-md-4">
              <a class="btn btn-sm btn-outline-secondary mr-3" @click="goBack">
                <i class="fa fa-arrow-left mr-1"></i> Back to Queue
              </a>
              <strong class="h5 mb-0">{{ detail.business.business_name }}</strong>
              <div class="text-muted mt-1">{{ detail.business.country }} &middot; {{ detail.business.business_type }}</div>
            </div>
            <div class="col-md-4 text-center">
              <span class="badge mr-2" :class="statusBadgeClass(detail.submission.status)">
                {{ statusLabel(detail.submission.status) }}
              </span>
              <span :class="slaTextClass">
                SLA: {{ formatDate(detail.submission.sla_deadline) }}
                <span v-if="slaDaysLeft !== null" class="ml-1">({{ slaDaysLeftText }})</span>
              </span>
            </div>
            <div class="col-md-4 text-right">
              <span class="mr-3">
                Tier 1: <span class="badge" :class="tierBadgeClass(detail.submission.tier1_status || 'pending')">{{ tierLabel(detail.submission.tier1_status || 'pending') }}</span>
              </span>
              <span class="mr-3">
                Tier 2: <span class="badge" :class="tierBadgeClass(detail.submission.tier2_status || 'pending')">{{ tierLabel(detail.submission.tier2_status || 'pending') }}</span>
              </span>
              <b-button
                v-if="!detail.submission.assigned_to"
                variant="primary"
                size="sm"
                :disabled="assigningReviewer"
                @click="assignToMe"
              >
                <b-spinner v-if="assigningReviewer" small class="mr-1"></b-spinner>
                <i v-else class="fa fa-user-plus mr-1"></i> Assign to Me
              </b-button>
              <span v-else class="text-muted">
                <i class="fa fa-user mr-1"></i> Assigned
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Layout: Section Nav + Content -->
      <div class="row">
        <!-- Left Sidebar - Section Navigator -->
        <div class="col-lg-3 col-md-4">
          <div class="card">
            <div class="header"><h2>Sections</h2></div>
            <div class="body pt-0">
              <ul class="list-unstyled section-nav">
                <li
                  v-for="section in detail.sections"
                  :key="section.id"
                  class="section-nav-item"
                  :class="{ active: activeSection === section.section_code }"
                  @click="selectSection(section.section_code)"
                >
                  <span class="section-icon mr-2" :class="sectionIconClass(section.status)">
                    {{ sectionIcon(section.status) }}
                  </span>
                  {{ section.name }}
                </li>
                <li
                  class="section-nav-item"
                  :class="{ active: activeSection === 'audit_log' }"
                  @click="selectSection('audit_log')"
                >
                  <span class="section-icon mr-2 text-muted"><i class="fa fa-history"></i></span>
                  Audit Log
                </li>
              </ul>

              <hr />

              <div class="text-muted small">
                <div>Sections: {{ approvedSectionsCount }}/{{ detail.sections.length }} approved</div>
                <div>Documents: {{ approvedDocsCount }}/{{ totalDocsCount }} approved</div>
                <div>Declarations: {{ allDeclsAcknowledged ? '&#10003; All acknowledged' : 'Incomplete' }}</div>
              </div>

              <hr />

              <b-button
                :variant="isFullyApproved ? 'outline-success' : 'primary'"
                block
                :disabled="!canOpenDecisionModal"
                @click="showFinalDecisionModal = true"
              >
                <i v-if="isFullyApproved" class="fa fa-eye mr-1"></i>
                {{ approveButtonLabel }}
              </b-button>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="col-lg-9 col-md-8">
          <!-- Section Review Panel -->
          <template v-if="currentSectionData && activeSection !== 'audit_log'">
            <div class="card">
              <div class="header d-flex justify-content-between align-items-center">
                <h2>{{ currentSectionData.name }}</h2>
                <span class="badge" :class="sectionStatusBadgeClass(currentSectionData.status)">
                  {{ sectionStatusLabel(currentSectionData.status) }}
                </span>
              </div>
              <div class="body">
                <p class="text-muted mb-3">{{ currentSectionData.description }}</p>

                <!-- Documents Tab -->
                <template v-if="currentSectionData.section_code === 'supporting_documents'">
                  <h5 class="mb-3">Business Documents</h5>
                  <div
                    v-for="doc in detail.documents.business_documents"
                    :key="doc.document_type_code"
                    class="card border mb-3"
                  >
                    <div class="body">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 class="mb-1">
                            {{ getDocumentName(doc) }}
                            <span v-if="doc.is_mandatory" class="badge badge-default ml-1">MANDATORY</span>
                          </h6>
                        </div>
                      </div>
                      <template v-if="doc.uploaded && doc.uploaded.length > 0">
                        <div v-for="file in doc.uploaded" :key="file.id" class="mt-2 p-2 bg-light rounded">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <i class="fa fa-file mr-1" :class="file.mime_type.includes('pdf') ? 'fa-file-pdf-o text-danger' : 'fa-file-image-o text-primary'"></i>
                              <strong>{{ file.file_name }}</strong>
                              <span class="text-muted ml-2">{{ formatFileSize(file.file_size) }}</span>
                              <span class="text-muted ml-2">v{{ file.version }}</span>
                              <span v-if="doc.has_expiry && file.expiry_date" class="ml-2">
                                Expires: {{ formatDate(file.expiry_date) }}
                              </span>
                            </div>
                            <div>
                              <b-button
                                v-if="file.file_url"
                                variant="outline-primary"
                                size="sm"
                                class="mr-2"
                                @click="viewDocument(file)"
                              >
                                <i class="fa fa-eye mr-1"></i> View
                              </b-button>
                              <span class="badge mr-2" :class="docStatusBadgeClass(file.status)">
                                {{ docStatusLabel(file.status) }}
                              </span>
                              <b-button
                                variant="outline-success"
                                size="sm"
                                class="mr-1"
                                @click="approveDocument(file)"
                                :disabled="!canReviewSections || file.status === 'approved' || actionInProgress === `doc-approve-${file.id}`"
                              >
                                <b-spinner v-if="actionInProgress === `doc-approve-${file.id}`" small></b-spinner>
                                <i v-else class="fa fa-check"></i>
                              </b-button>
                              <b-button
                                variant="outline-danger"
                                size="sm"
                                @click="openRejectModal('document', file)"
                                :disabled="!canReviewSections || file.status === 'rejected'"
                              >
                                <i class="fa fa-times"></i>
                              </b-button>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="mt-2 p-2 bg-warning-light rounded text-warning">
                          <i class="fa fa-exclamation-triangle mr-1"></i> Not uploaded
                        </div>
                      </template>
                    </div>
                  </div>

                  <!-- Missing Documents -->
                  <template v-if="missingDocuments.length > 0">
                    <h6 class="text-warning mt-3">Missing Documents</h6>
                    <div v-for="doc in missingDocuments" :key="'missing-' + doc.document_type_code" class="alert alert-warning py-2 mb-2">
                      <i class="fa fa-exclamation-triangle mr-1"></i> {{ getDocumentName(doc) }} — MANDATORY &middot; NOT UPLOADED
                    </div>
                  </template>

                  <!-- Stakeholder Documents -->
                  <template v-if="detail.documents.stakeholder_documents.length > 0">
                    <h5 class="mt-4 mb-3">Stakeholder Documents</h5>
                    <div
                      v-for="sh in detail.documents.stakeholder_documents"
                      :key="'sh-' + sh.stakeholder_id"
                      class="card border mb-3"
                    >
                      <div class="header">
                        <h2><i class="fa fa-user mr-1"></i> {{ sh.stakeholder_name }} — {{ sh.stakeholder_type }} </h2>
                      </div>
                      <div class="body">
                        <div v-for="doc in sh.documents" :key="doc.document_type_code" class="mb-3">
                          <div class="d-flex justify-content-between align-items-center">
                            <strong>{{ getDocumentName(doc) }}</strong>
                          </div>
                          <template v-if="doc.uploaded && doc.uploaded.length > 0">
                            <div v-for="file in doc.uploaded" :key="file.id" class="mt-1 p-2 bg-light rounded">
                              <div class="d-flex justify-content-between align-items-center">
                                <div>
                                  <i class="fa mr-1" :class="file.mime_type.includes('pdf') ? 'fa-file-pdf-o text-danger' : 'fa-file-image-o text-primary'"></i>
                                  {{ file.file_name }}
                                  <span class="text-muted ml-2">{{ formatFileSize(file.file_size) }}</span>
                                </div>
                                <div>
                                  <b-button
                                    v-if="file.file_url"
                                    variant="outline-primary"
                                    size="sm"
                                    class="mr-2"
                                    @click="viewDocument(file)"
                                  >
                                    <i class="fa fa-eye mr-1"></i> View
                                  </b-button>
                                  <span class="badge mr-2" :class="docStatusBadgeClass(file.status)">{{ docStatusLabel(file.status) }}</span>
                                  <b-button
                                    variant="outline-success"
                                    size="sm"
                                    class="mr-1"
                                    @click="approveDocument(file)"
                                    :disabled="!canReviewSections || file.status === 'approved' || actionInProgress === `doc-approve-${file.id}`"
                                  >
                                    <b-spinner v-if="actionInProgress === `doc-approve-${file.id}`" small></b-spinner>
                                    <i v-else class="fa fa-check"></i>
                                  </b-button>
                                  <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    @click="openRejectModal('document', file)"
                                    :disabled="!canReviewSections || file.status === 'rejected'"
                                  >
                                    <i class="fa fa-times"></i>
                                  </b-button>
                                </div>
                              </div>
                            </div>
                          </template>
                          <template v-else>
                            <div class="mt-1 p-2 bg-warning-light rounded text-warning">
                              <i class="fa fa-exclamation-triangle mr-1"></i> Not uploaded
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>

                <!-- Regular Section Data Display (schema-driven) -->
                <template v-else>
                  <div v-if="!currentSectionData.data || !Object.keys(currentSectionData.data).length" class="text-center text-muted py-4">
                    No data submitted for this section.
                  </div>
                  <div v-else class="row">
                    <template v-for="field in sectionDisplayFields">
                      <!-- Boolean -->
                      <div v-if="field.type === 'boolean'" :key="field.key" class="col-md-6 mb-3">
                        <label class="text-muted small mb-0">{{ field.label }}</label>
                        <div class="p-2 bg-light rounded">
                          <span :class="field.value ? 'text-success' : 'text-danger'">
                            {{ field.value ? 'Yes' : 'No' }}
                          </span>
                        </div>
                      </div>

                      <!-- Reference object (country, etc.) — show name only -->
                      <div v-else-if="isReferenceObject(field.value)" :key="field.key" class="col-md-6 mb-3">
                        <label class="text-muted small mb-0">{{ field.label }}</label>
                        <div class="p-2 bg-light rounded">{{ formatReferenceObject(field.value) }}</div>
                      </div>

                      <!-- Date object ({ day, month, year }) — show formatted date -->
                      <div v-else-if="isDateObject(field.value)" :key="field.key" class="col-md-6 mb-3">
                        <label class="text-muted small mb-0">{{ field.label }}</label>
                        <div class="p-2 bg-light rounded">{{ formatDateObject(field.value) }}</div>
                      </div>

                      <!-- Nested Object (address, contact, etc.) -->
                      <div v-else-if="isObjectValue(field.value)" :key="field.key" class="col-12 mb-3">
                        <label class="text-muted small mb-1 d-block"><strong>{{ field.label }}</strong></label>
                        <div class="p-3 bg-light rounded">
                          <div class="row">
                            <div
                              v-for="(subVal, subKey) in filterNonNull(field.value)"
                              :key="subKey"
                              class="col-md-6 mb-2"
                            >
                              <label class="text-muted small mb-0">{{ getNestedLabel(field.schema, subKey) }}</label>
                              <div>{{ formatDisplayValue(subVal) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Array of Objects -->
                      <div v-else-if="isArrayOfObjects(field.value)" :key="field.key" class="col-12 mb-3">
                        <label class="text-muted small mb-1 d-block"><strong>{{ field.label }}</strong></label>
                        <div
                          v-for="(item, idx) in field.value"
                          :key="field.key + '-' + idx"
                          class="p-3 bg-light rounded mb-2"
                        >
                          <div class="row">
                            <div
                              v-for="(subVal, subKey) in filterNonNull(item)"
                              :key="subKey"
                              class="col-md-4 mb-2"
                            >
                              <label class="text-muted small mb-0">{{ formatFieldKey(subKey) }}</label>
                              <div>{{ formatDisplayValue(subVal) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Array of Simple Values -->
                      <div v-else-if="Array.isArray(field.value)" :key="field.key" class="col-md-6 mb-3">
                        <label class="text-muted small mb-0">{{ field.label }}</label>
                        <div class="p-2 bg-light rounded">{{ field.value.join(', ') || '—' }}</div>
                      </div>

                      <!-- Simple value (string, number, integer) -->
                      <div v-else :key="field.key" class="col-md-6 mb-3">
                        <label class="text-muted small mb-0">{{ field.label }}</label>
                        <div class="p-2 bg-light rounded">{{ formatDisplayValue(field.value) }}</div>
                      </div>
                    </template>
                  </div>

                  <!-- Stakeholders list (shown under controllers section) -->
                  <template v-if="currentSectionData.section_code === 'controllers' && detail.stakeholders && detail.stakeholders.length">
                    <hr />
                    <h5 class="mb-3">Stakeholders</h5>
                    <div
                      v-for="sh in detail.stakeholders"
                      :key="'sh-' + sh.id"
                      class="card border mb-3"
                    >
                      <div class="header d-flex justify-content-between align-items-center">
                        <h2><i class="fa fa-user mr-1"></i> {{ sh.first_name }} {{ sh.last_name }}</h2>
                        <span class="badge badge-info">{{ sh.stakeholder_type }}</span>
                      </div>
                      <div class="body">
                        <div class="row">
                          <div class="col-md-6 mb-2">
                            <label class="text-muted small mb-0">Full Name</label>
                            <div>{{ sh.first_name }} {{ sh.last_name }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.date_of_birth">
                            <label class="text-muted small mb-0">Date of Birth</label>
                            <div>{{ sh.date_of_birth }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.nationality">
                            <label class="text-muted small mb-0">Nationality</label>
                            <div>{{ sh.nationality }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.id_number">
                            <label class="text-muted small mb-0">ID Number</label>
                            <div>{{ sh.id_number }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.email">
                            <label class="text-muted small mb-0">Email</label>
                            <div>{{ sh.email }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.phone">
                            <label class="text-muted small mb-0">Phone</label>
                            <div>{{ sh.phone }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.ownership_percentage != null">
                            <label class="text-muted small mb-0">Ownership</label>
                            <div>{{ sh.ownership_percentage }}%</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.role_title">
                            <label class="text-muted small mb-0">Role</label>
                            <div>{{ sh.role_title }}</div>
                          </div>
                          <div class="col-md-6 mb-2" v-if="sh.is_pep">
                            <label class="text-muted small mb-0">PEP Status</label>
                            <div class="text-danger font-weight-bold">
                              <i class="fa fa-exclamation-triangle mr-1"></i>
                              Yes — "{{ sh.pep_details }}"
                            </div>
                          </div>
                          <div class="col-12 mb-2" v-if="sh.address">
                            <label class="text-muted small mb-0">Address</label>
                            <div>{{ formatAddress(sh.address) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>

                <!-- Declarations list (shown under declarations section) -->
                <template v-if="currentSectionData.section_code === 'declarations' && declarationsList.length">
                  <div
                    v-for="decl in declarationsList"
                    :key="decl.declaration_code"
                    class="d-flex align-items-start mb-3 p-2 rounded"
                    :class="decl.acknowledged ? 'bg-light' : 'bg-danger-light'"
                  >
                    <span class="mr-2 mt-1" style="font-size: 18px;">
                      <template v-if="decl.acknowledged"><span class="text-success">&#10003;</span></template>
                      <template v-else><span class="text-danger">&#10007;</span></template>
                    </span>
                    <div>
                      <div class="font-weight-bold">{{ decl.declaration_text }}</div>
                      <div class="text-muted small" v-if="decl.acknowledged">
                        Acknowledged: {{ formatDateTime(decl.acknowledged_at) }}
                      </div>
                      <div class="text-danger small" v-else>
                        NOT ACKNOWLEDGED
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Section Approve / Reject bar -->
                <hr />
                <div class="d-flex justify-content-end">
                  <b-button
                    variant="success"
                    class="mr-2"
                    @click="approveSection(currentSectionData)"
                    :disabled="!canReviewSections || currentSectionData.status === 'approved' || actionInProgress === `section-approve-${currentSectionData.id}`"
                  >
                    <b-spinner v-if="actionInProgress === `section-approve-${currentSectionData.id}`" small class="mr-1"></b-spinner>
                    <i v-else class="fa fa-check mr-1"></i> Approve Section
                  </b-button>
                  <b-button
                    variant="danger"
                    @click="openRejectModal('section', currentSectionData)"
                    :disabled="!canReviewSections || currentSectionData.status === 'rejected'"
                  >
                    <i class="fa fa-times mr-1"></i> Reject Section
                  </b-button>
                </div>
              </div>
            </div>
          </template>

          <!-- Audit Log Tab -->
          <template v-if="activeSection === 'audit_log'">
            <div class="card">
              <div class="header"><h2>Audit Log</h2></div>
              <div class="body">
                <div v-if="auditLogLoading" class="text-center py-4">
                  <b-spinner variant="primary" small></b-spinner>
                  <span class="text-muted ml-2">Loading audit log...</span>
                </div>
                <template v-else>
                  <div
                    v-for="entry in auditLog"
                    :key="entry.id"
                    class="d-flex align-items-start mb-3 pb-3 border-bottom"
                  >
                    <div class="mr-3 text-muted" style="min-width: 140px; font-size: 12px;">
                      {{ formatDateTime(entry.created_at) }}
                    </div>
                    <div>
                      <div class="font-weight-bold">{{ entry.actor_name }}</div>
                      <div>{{ formatAuditAction(entry) }}</div>
                    </div>
                  </div>
                  <div v-if="auditLog.length === 0" class="text-center text-muted py-4">
                    No audit log entries.
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Rejection Modal -->
      <b-modal v-model="showRejectModal" title="Reject" @ok="confirmReject" ok-title="Confirm Rejection" ok-variant="danger">
        <template #modal-title>
          Reject {{ rejectTarget.type === 'section' ? 'Section' : 'Document' }}: {{ rejectTarget.name }}
        </template>
        <b-form-group label="Rejection Category" label-for="reject-category">
          <b-form-select
            id="reject-category"
            v-model="rejectForm.category_id"
            :options="filteredRejectionCategories"
            value-field="id"
            text-field="name"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>Select a category...</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <div v-if="selectedRejectionCategory" class="alert alert-info small mb-3">
          <strong>Customer will see:</strong> "{{ selectedRejectionCategory.customer_message }}"
        </div>
        <b-form-group label="Reason / Notes" label-for="reject-reason">
          <b-form-textarea
            id="reject-reason"
            v-model="rejectForm.reason"
            rows="3"
            :required="selectedRejectionCategory && selectedRejectionCategory.requires_free_text"
            :placeholder="selectedRejectionCategory && selectedRejectionCategory.requires_free_text ? 'Required — please provide details' : 'Optional notes'"
          ></b-form-textarea>
        </b-form-group>
        <div v-if="actionInProgress === 'rejecting'" class="text-center">
          <b-spinner small variant="danger"></b-spinner> Submitting...
        </div>
      </b-modal>

      <!-- Final Decision Modal -->
      <b-modal
        v-model="showFinalDecisionModal"
        :title="approveButtonLabel"
        size="lg"
        @ok="submitFinalDecision"
        :ok-title="isFullyApproved ? 'Close' : modalSubmitLabel"
        :ok-variant="isFullyApproved ? 'secondary' : modalSubmitVariant"
        :ok-disabled="!isFullyApproved && !modalFormValid"
        :ok-only="isFullyApproved"
        @show="resetTier2Form"
      >
        <template #modal-title>
          {{ approveButtonLabel }}
        </template>

        <!-- Fully Approved: Read-only view -->
        <template v-if="isFullyApproved">
          <div class="alert alert-success mb-3">
            <i class="fa fa-check-circle mr-2"></i>
            This business has been fully approved.
          </div>

          <template v-if="approvalTier">
            <!-- Approved Products -->
            <div class="card border mb-3">
              <div class="header py-2 px-3">
                <h2 class="mb-0"><i class="fa fa-cube mr-2"></i>Approved Products</h2>
              </div>
              <div class="body pt-0">
                <span
                  v-for="product in approvalTier.approved_products"
                  :key="product.id"
                  class="badge badge-success mr-2 mb-1"
                  style="font-size: 13px;"
                >
                  {{ product.name }}
                </span>
                <span v-if="!approvalTier.approved_products || approvalTier.approved_products.length === 0" class="text-muted">None</span>
              </div>
            </div>

            <!-- Approved Countries -->
            <div class="card border mb-3">
              <div class="header py-2 px-3">
                <h2 class="mb-0"><i class="fa fa-globe mr-2"></i>Approved Countries</h2>
              </div>
              <div class="body pt-0">
                <span
                  v-for="country in approvalTier.approved_countries"
                  :key="country.id"
                  class="badge badge-primary mr-2 mb-1"
                  style="font-size: 13px;"
                >
                  {{ country.name }}
                </span>
                <span v-if="!approvalTier.approved_countries || approvalTier.approved_countries.length === 0" class="text-muted">None</span>
              </div>
            </div>

            <!-- Volume Limits -->
            <div class="card border mb-3">
              <div class="header py-2 px-3">
                <h2 class="mb-0"><i class="fa fa-bar-chart mr-2"></i>Volume Limits</h2>
              </div>
              <div class="body pt-0">
                <template v-if="approvalTier.volume_limits">
                  <div class="row">
                    <div class="col-md-4">
                      <label class="text-muted small mb-0">Per Transaction</label>
                      <div class="font-weight-bold">{{ approvalTier.volume_limits.per_transaction ? '$' + Number(approvalTier.volume_limits.per_transaction).toLocaleString() : 'No limit' }}</div>
                    </div>
                    <div class="col-md-4">
                      <label class="text-muted small mb-0">Daily</label>
                      <div class="font-weight-bold">{{ approvalTier.volume_limits.daily ? '$' + Number(approvalTier.volume_limits.daily).toLocaleString() : 'No limit' }}</div>
                    </div>
                    <div class="col-md-4">
                      <label class="text-muted small mb-0">Monthly</label>
                      <div class="font-weight-bold">{{ approvalTier.volume_limits.monthly ? '$' + Number(approvalTier.volume_limits.monthly).toLocaleString() : 'No limit' }}</div>
                    </div>
                  </div>
                </template>
                <span v-else class="text-muted">No volume limits set</span>
              </div>
            </div>
          </template>
          <div v-else class="text-muted">Approval details will be available after the page is refreshed.</div>
        </template>

        <!-- Review Summary (both tiers, only when not fully approved) -->
        <template v-else>
        <div class="card bg-light mb-3">
          <div class="body p-3">
            <strong>Review Summary</strong>
            <div class="mt-2">
              Sections: {{ approvedSectionsCount }}/{{ detail.sections.length }} approved
              <span v-if="rejectedSectionsCount > 0"> &middot; {{ rejectedSectionsCount }} rejected</span>
            </div>
            <div>Documents: {{ approvedDocsCount }}/{{ totalDocsCount }} approved</div>
            <div>Declarations: {{ allDeclsAcknowledged ? 'All acknowledged' : 'Incomplete' }}</div>
          </div>
        </div>

        <!-- Tier 1: Decision -->
        <template v-if="!isTier1Approved">
          <b-form-group label="Decision" class="mb-3">
            <b-form-radio-group v-model="finalDecisionForm.decision" stacked>
              <b-form-radio value="approved" :disabled="!canApproveTier">
                Approve — Pass to Tier 2 review
                <span v-if="!canApproveTier" class="text-danger small ml-1">(all sections must be approved first)</span>
              </b-form-radio>
              <b-form-radio value="changes_requested">Request Changes — Send back to business for corrections</b-form-radio>
              <b-form-radio value="rejected">Reject — Decline the application</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group
            v-if="finalDecisionForm.decision === 'changes_requested' || finalDecisionForm.decision === 'rejected'"
            label="Reason"
            class="mb-3"
          >
            <b-form-textarea
              v-model="finalDecisionForm.reason"
              rows="3"
              required
              :placeholder="finalDecisionForm.decision === 'rejected' ? 'Explain why the application is being declined.' : 'Explain what needs to change.'"
            ></b-form-textarea>
          </b-form-group>
        </template>

        <!-- Tier 2: Compliance restrictions & checklist -->
        <template v-else>
          <p class="text-muted mb-3">Review compliance and configure restrictions before making a decision.</p>

          <!-- Tier 2 Decision -->
          <b-form-group label="Decision" class="mb-3">
            <b-form-radio-group v-model="tier2Form.decision" stacked>
              <b-form-radio value="approved">Approve — Fully approve this business</b-form-radio>
              <b-form-radio value="changes_requested">Request Changes — Send back to business for corrections</b-form-radio>
              <b-form-radio value="rejected">Reject — Decline the application</b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <b-form-group
            v-if="tier2Form.decision === 'changes_requested' || tier2Form.decision === 'rejected'"
            label="Reason"
            class="mb-3"
          >
            <b-form-textarea
              v-model="tier2Form.reason"
              rows="3"
              required
              :placeholder="tier2Form.decision === 'rejected' ? 'Explain why the application is being declined.' : 'Explain what needs to change.'"
            ></b-form-textarea>
          </b-form-group>

          <!-- Only show compliance config when approving -->
          <template v-if="tier2Form.decision === 'approved'">

          <!-- Compliance Checklist -->
          <div class="card border mb-3">
            <div class="header py-2 px-3">
              <h2 class="mb-0"><i class="fa fa-check-square-o mr-2"></i>Compliance Checklist</h2>
            </div>
            <div class="body pt-0">
              <div class="custom-control custom-checkbox mb-2">
                <input type="checkbox" class="custom-control-input" id="check-kyb" v-model="tier2Form.checklist.kyb_kyc_complete" />
                <label class="custom-control-label" for="check-kyb">KYB/KYC checks complete</label>
              </div>
              <div class="custom-control custom-checkbox mb-2">
                <input type="checkbox" class="custom-control-input" id="check-sanctions" v-model="tier2Form.checklist.sanctions_pep_screening" />
                <label class="custom-control-label" for="check-sanctions">Sanctions &amp; PEP screening done</label>
              </div>
              <div class="custom-control custom-checkbox mb-2">
                <input type="checkbox" class="custom-control-input" id="check-risk" v-model="tier2Form.checklist.risk_assessment_recorded" />
                <label class="custom-control-label" for="check-risk">Risk assessment recorded</label>
              </div>
              <div class="custom-control custom-checkbox mb-2">
                <input type="checkbox" class="custom-control-input" id="check-docs" v-model="tier2Form.checklist.required_docs_approved" />
                <label class="custom-control-label" for="check-docs">Required documents approved</label>
              </div>
              <div v-if="!tier2ChecklistComplete" class="text-danger small mt-2">
                <i class="fa fa-exclamation-circle mr-1"></i> All checklist items must be confirmed before approval.
              </div>
            </div>
          </div>

          <!-- Approved Products -->
          <div class="card border mb-3">
            <div class="header py-2 px-3">
              <h2 class="mb-0"><i class="fa fa-cube mr-2"></i>Approved Products</h2>
            </div>
            <div class="body pt-0">
              <p class="text-muted small mb-2">Select which products this business is allowed to use.</p>
              <div v-if="availableProducts.length === 0" class="text-muted small py-2">
                <b-spinner small class="mr-1"></b-spinner> Loading products...
              </div>
              <div v-for="product in availableProducts" :key="product.id" class="custom-control custom-checkbox mb-2">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`product-${product.id}`"
                  :value="product.id"
                  v-model="tier2Form.approvedProducts"
                />
                <label class="custom-control-label" :for="`product-${product.id}`">{{ product.name }}</label>
              </div>
              <div v-if="tier2Form.approvedProducts.length === 0" class="text-danger small mt-2">
                <i class="fa fa-exclamation-circle mr-1"></i> At least one product must be selected.
              </div>
            </div>
          </div>

          <!-- Allowed Countries / Corridors -->
          <div class="card border mb-3">
            <div class="header py-2 px-3">
              <h2 class="mb-0"><i class="fa fa-globe mr-2"></i>Allowed Countries / Corridors</h2>
            </div>
            <div class="body pt-0">
              <p class="text-muted small mb-2">Select which countries or corridors this business can operate in.</p>
              <div class="mb-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Search countries..."
                  v-model="countrySearch"
                />
              </div>
              <div v-if="availableCountries.length === 0" class="text-muted small py-2">
                <b-spinner small class="mr-1"></b-spinner> Loading countries...
              </div>
              <div v-else style="max-height: 200px; overflow-y: auto;" class="border rounded p-2">
                <div v-for="country in filteredCountries" :key="country.id" class="custom-control custom-checkbox mb-1">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="`country-${country.id}`"
                    :value="country.id"
                    v-model="tier2Form.approvedCountries"
                  />
                  <label class="custom-control-label" :for="`country-${country.id}`">{{ country.name }}</label>
                </div>
                <div v-if="filteredCountries.length === 0" class="text-muted small py-2 text-center">
                  No countries match your search.
                </div>
              </div>
              <div v-if="tier2Form.approvedCountries.length > 0" class="mt-2">
                <span
                  v-for="countryId in tier2Form.approvedCountries"
                  :key="'tag-' + countryId"
                  class="badge badge-primary mr-1 mb-1"
                  style="cursor: pointer;"
                  @click="removeCountry(countryId)"
                >
                  {{ getCountryName(countryId) }} <i class="fa fa-times ml-1"></i>
                </span>
              </div>
              <div v-else class="text-danger small mt-2">
                <i class="fa fa-exclamation-circle mr-1"></i> At least one country must be selected.
              </div>
            </div>
          </div>

          <!-- Volume Limits -->
          <div class="card border mb-3">
            <div class="header py-2 px-3">
              <h2 class="mb-0"><i class="fa fa-bar-chart mr-2"></i>Volume Limits</h2>
            </div>
            <div class="body pt-0">
              <p class="text-muted small mb-2">Set transaction volume limits for this business. Leave blank for no limit.</p>
              <div class="row">
                <div class="col-md-4">
                  <b-form-group label="Per Transaction (USD)" label-class="small text-muted">
                    <b-form-input
                      v-model.number="tier2Form.volumeLimits.per_transaction"
                      type="number"
                      min="0"
                      placeholder="No limit"
                      size="sm"
                    ></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <b-form-group label="Daily (USD)" label-class="small text-muted">
                    <b-form-input
                      v-model.number="tier2Form.volumeLimits.daily"
                      type="number"
                      min="0"
                      placeholder="No limit"
                      size="sm"
                    ></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <b-form-group label="Monthly (USD)" label-class="small text-muted">
                    <b-form-input
                      v-model.number="tier2Form.volumeLimits.monthly"
                      type="number"
                      min="0"
                      placeholder="No limit"
                      size="sm"
                    ></b-form-input>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
          </template>
        </template>
        </template>

        <div v-if="actionInProgress === 'final-decision'" class="text-center">
          <b-spinner small variant="primary"></b-spinner> Submitting decision...
        </div>
      </b-modal>
    </template>
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb.vue";
import { mapGetters } from "vuex";

export default {
  name: "SubmissionDetail",
  components: {
    "bread-crumb": BreadCrumb,
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Onboarding", href: "/onboarding/submissions" },
        { title: "Submission Detail" },
      ],
      activeSection: "basic_business_info",
      auditLogLoading: false,
      actionInProgress: null,
      assigningReviewer: false,

      showRejectModal: false,
      rejectTarget: { type: null, name: "", item: null },
      rejectForm: { category_id: null, reason: "" },

      showFinalDecisionModal: false,
      finalDecisionForm: { decision: null, reason: "" },

      // Tier 2 compliance form
      tier2Form: {
        decision: null,
        reason: "",
        approvedProducts: [],
        approvedCountries: [],
        volumeLimits: {
          daily: null,
          monthly: null,
          per_transaction: null,
        },
        checklist: {
          kyb_kyc_complete: false,
          sanctions_pep_screening: false,
          risk_assessment_recorded: false,
          required_docs_approved: false,
        },
      },
      countrySearch: "",
    };
  },
  computed: {
    ...mapGetters("onboarding", ["currentSubmission", "rejectionCategories", "auditLog", "isLoading", "availableProducts", "availableCountries"]),
    storeError() {
      return this.$store.getters["onboarding/error"];
    },
    detail() {
      return this.currentSubmission;
    },
    submissionId() {
      return this.$route.params.submissionId;
    },
    currentUserId() {
      const user = this.$store.getters.currentuser;
      return user?.userid || user?.id || null;
    },
    currentSectionData() {
      if (!this.detail) return null;
      return this.detail.sections.find((s) => s.section_code === this.activeSection) || null;
    },
    sectionDisplayFields() {
      if (!this.currentSectionData) return [];
      const schema = this.currentSectionData.field_schema || {};
      const data = this.currentSectionData.data;
      if (!data || !Object.keys(data).length) return [];
      const properties = schema.properties || {};
      const fields = [];
      for (const [key, value] of Object.entries(data)) {
        if (value === null || value === undefined) continue;
        const propSchema = properties[key] || {};
        fields.push({
          key,
          label: propSchema.title || this.formatFieldKey(key),
          value,
          type: propSchema.type || this.inferFieldType(value),
          schema: propSchema,
        });
      }
      return fields;
    },
    approvedSectionsCount() {
      if (!this.detail) return 0;
      return this.detail.sections.filter((s) => s.status === "approved").length;
    },
    rejectedSectionsCount() {
      if (!this.detail) return 0;
      return this.detail.sections.filter((s) => s.status === "rejected").length;
    },
    allDocsFlat() {
      if (!this.detail) return [];
      const docs = [];
      this.detail.documents.business_documents.forEach((d) => {
        d.uploaded.forEach((f) => docs.push(f));
      });
      this.detail.documents.stakeholder_documents.forEach((sh) => {
        sh.documents.forEach((d) => {
          d.uploaded.forEach((f) => docs.push(f));
        });
      });
      return docs;
    },
    approvedDocsCount() {
      return this.allDocsFlat.filter((f) => f.status === "approved").length;
    },
    totalDocsCount() {
      return this.allDocsFlat.length;
    },
    declarationsList() {
      if (!this.detail) return [];
      const declSection = this.detail.sections.find((s) => s.section_code === "declarations");
      if (!declSection || !declSection.data) return [];
      const data = declSection.data;
      if (Array.isArray(data)) return data;
      if (data.declarations && Array.isArray(data.declarations)) return data.declarations;
      return [];
    },
    allDeclsAcknowledged() {
      if (!this.declarationsList.length) return false;
      return this.declarationsList.every((d) => d.acknowledged);
    },
    missingDocuments() {
      if (!this.detail) return [];
      return this.detail.documents.business_documents.filter(
        (d) => d.is_mandatory && (!d.uploaded || d.uploaded.length === 0)
      );
    },
    isAssigned() {
      return !!(this.detail && this.detail.submission.assigned_to);
    },
    isTier1Approved() {
      return !!(this.detail && this.detail.submission.tier1_status === "approved");
    },
    isTier2Approved() {
      return !!(this.detail && this.detail.submission.tier2_status === "approved");
    },
    isFullyApproved() {
      return this.isTier1Approved && this.isTier2Approved;
    },
    approvalTier() {
      if (!this.detail) return null;
      // Check multiple possible locations in the response
      return this.detail.approval_tier || this.detail.submission?.approval_tier || null;
    },
    hasTier1Permission() {
      return this.$store.getters["authpermission/hasPermission"]("tier1.approval");
    },
    hasTier2Permission() {
      return this.$store.getters["authpermission/hasPermission"]("tier2.approval");
    },
    canReviewSections() {
      if (!this.isAssigned || this.isFullyApproved) return false;
      if (this.isTier1Approved) return this.hasTier2Permission;
      return this.hasTier1Permission;
    },
    allSectionsApproved() {
      if (!this.detail) return false;
      return this.detail.sections.every((s) => s.status === "approved");
    },
    allMandatoryDocsReviewed() {
      if (!this.detail) return false;
      return this.detail.documents.business_documents
        .filter((d) => d.is_mandatory && d.uploaded.length > 0)
        .every((d) => d.uploaded.every((f) => f.status === "approved" || f.status === "rejected"));
    },
    canOpenDecisionModal() {
      if (!this.detail) return false;
      if (this.isFullyApproved) return true; // allow viewing approval details
      if (!this.isAssigned) return false;
      if (this.isTier1Approved) return this.hasTier2Permission;
      return this.hasTier1Permission;
    },
    canApproveTier() {
      return this.allSectionsApproved && this.allMandatoryDocsReviewed;
    },
    currentTierLabel() {
      if (!this.detail) return "Tier 1";
      return this.isTier1Approved ? "Tier 2" : "Tier 1";
    },
    approveButtonLabel() {
      if (!this.detail) return "Tier 1 Decision";
      if (this.isFullyApproved) return "View Approval Details";
      return this.isTier1Approved ? "Tier 2 Decision" : "Tier 1 Decision";
    },
    tier2ChecklistComplete() {
      const c = this.tier2Form.checklist;
      return c.kyb_kyc_complete && c.sanctions_pep_screening && c.risk_assessment_recorded && c.required_docs_approved;
    },
    tier2FormValid() {
      if (!this.isTier1Approved) return true; // not relevant for tier 1
      if (!this.tier2Form.decision) return false;
      if (this.tier2Form.decision === "changes_requested" || this.tier2Form.decision === "rejected") {
        return !!this.tier2Form.reason?.trim();
      }
      // For approval: checklist + products + countries required
      return (
        this.tier2ChecklistComplete &&
        this.tier2Form.approvedProducts.length > 0 &&
        this.tier2Form.approvedCountries.length > 0
      );
    },
    filteredCountries() {
      if (!this.countrySearch) return this.availableCountries;
      const search = this.countrySearch.toLowerCase();
      return this.availableCountries.filter((c) => c.name.toLowerCase().includes(search));
    },
    currentDecision() {
      return this.isTier1Approved ? this.tier2Form.decision : this.finalDecisionForm.decision;
    },
    modalSubmitLabel() {
      const tier = this.isTier1Approved ? "Tier 2" : "Tier 1";
      if (this.currentDecision === "changes_requested") return "Request Changes";
      if (this.currentDecision === "rejected") return "Reject";
      if (this.currentDecision === "approved") return `Approve ${tier}`;
      return "Submit";
    },
    modalSubmitVariant() {
      if (this.currentDecision === "changes_requested") return "warning";
      if (this.currentDecision === "rejected") return "danger";
      if (this.currentDecision === "approved") return "success";
      return "primary";
    },
    tier1FormValid() {
      if (!this.finalDecisionForm.decision) return false;
      if (this.finalDecisionForm.decision === "approved") return this.canApproveTier;
      // changes_requested or rejected need a reason
      return !!this.finalDecisionForm.reason?.trim();
    },
    modalFormValid() {
      if (this.isTier1Approved) return this.tier2FormValid;
      return this.tier1FormValid;
    },
    filteredRejectionCategories() {
      const targetType = this.rejectTarget.type;
      return this.rejectionCategories.filter(
        (c) => c.applies_to === targetType || c.applies_to === "both"
      );
    },
    selectedRejectionCategory() {
      if (!this.rejectForm.category_id) return null;
      return this.rejectionCategories.find((c) => c.id === this.rejectForm.category_id);
    },
    slaDaysLeft() {
      if (!this.detail) return null;
      const deadline = new Date(this.detail.submission.sla_deadline);
      const now = new Date();
      return Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    },
    slaDaysLeftText() {
      if (this.slaDaysLeft < 0) return `${Math.abs(this.slaDaysLeft)} days overdue`;
      if (this.slaDaysLeft === 0) return "Due today";
      return `${this.slaDaysLeft} days left`;
    },
    slaTextClass() {
      if (this.slaDaysLeft < 0) return "text-danger font-weight-bold";
      if (this.slaDaysLeft <= 2) return "text-warning";
      return "text-success";
    },
  },
  created() {
    this.loadDetail();
    this.$store.dispatch("onboarding/fetchRejectionCategories").catch(() => {});
  },
  methods: {
    async loadDetail() {
      try {
        const detail = await this.$store.dispatch("onboarding/fetchSubmissionDetail", this.submissionId);
        if (detail?.sections?.length) {
          this.activeSection = detail.sections[0].section_code;
        }
      } catch {
        // Error is set in the store
      }
    },
    goBack() {
      this.$router.push("/onboarding/submissions");
    },
    selectSection(code) {
      this.activeSection = code;
      if (code === "audit_log") {
        this.loadAuditLog();
      }
    },
    async loadAuditLog() {
      this.auditLogLoading = true;
      try {
        await this.$store.dispatch("onboarding/fetchAuditLog", {
          submissionId: this.submissionId,
        });
      } catch {
        this.$bvToast.toast("Failed to load audit log.", { variant: "danger", solid: true });
      } finally {
        this.auditLogLoading = false;
      }
    },
    async assignToMe() {
      if (!this.currentUserId) {
        this.$bvToast.toast("Unable to determine your user ID.", { variant: "warning", solid: true });
        return;
      }
      this.assigningReviewer = true;
      try {
        await this.$store.dispatch("onboarding/assignReviewer", {
          submissionId: this.submissionId,
          assignedTo: this.currentUserId,
        });
        this.$bvToast.toast("Submission assigned to you.", { variant: "success", solid: true });
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || "Failed to assign submission.", { variant: "danger", solid: true });
      } finally {
        this.assigningReviewer = false;
      }
    },
    async approveSection(section) {
      if (!this.canReviewSections) {
        if (!this.isAssigned) {
          this.$bvToast.toast("Please assign this submission before approving.", { variant: "warning", solid: true });
        } else if (this.isTier1Approved) {
          this.$bvToast.toast("Tier 1 has been approved. Sections can no longer be modified.", { variant: "warning", solid: true });
        } else {
          this.$bvToast.toast("You do not have permission to review sections.", { variant: "warning", solid: true });
        }
        return;
      }
      const swalWithBootstrapButtons = this.$swal.mixin({
        customClass: {
          confirmButton: "btn btn-success btn-lg my-3",
          cancelButton: "btn btn-outline-secondary btn-lg",
        },
        buttonsStyling: false,
      });
      const result = await swalWithBootstrapButtons.fire({
        title: "Approve Section?",
        text: `Approve "${section.name}" for ${this.detail.business.business_name}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, approve it!",
      });
      if (!result.isConfirmed) return;
      this.actionInProgress = `section-approve-${section.id}`;
      try {
        await this.$store.dispatch("onboarding/reviewSection", {
          sectionId: section.id,
          decision: "approved",
        });
        swalWithBootstrapButtons.fire("Approved!", `${section.name} has been approved.`, "success");
      } catch (error) {
        swalWithBootstrapButtons.fire("Error!", error.response?.data?.message || "Failed to approve section.", "error");
      } finally {
        this.actionInProgress = null;
      }
    },
    openRejectModal(type, item) {
      if (!this.canReviewSections) {
        if (!this.isAssigned) {
          this.$bvToast.toast("Please assign this submission before rejecting.", { variant: "warning", solid: true });
        } else if (this.isTier1Approved) {
          this.$bvToast.toast("Tier 1 has been approved. Sections can no longer be modified.", { variant: "warning", solid: true });
        } else {
          this.$bvToast.toast("You do not have permission to review sections.", { variant: "warning", solid: true });
        }
        return;
      }
      this.rejectTarget = {
        type,
        name: type === "section" ? item.name : item.file_name,
        item,
      };
      this.rejectForm = { category_id: null, reason: "" };
      this.showRejectModal = true;
    },
    async confirmReject(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (!this.rejectForm.category_id) {
        this.$bvToast.toast("Please select a rejection category.", { variant: "warning", solid: true });
        return;
      }
      const cat = this.selectedRejectionCategory;
      if (cat && cat.requires_free_text && !this.rejectForm.reason.trim()) {
        this.$bvToast.toast("Please provide a reason.", { variant: "warning", solid: true });
        return;
      }
      this.actionInProgress = "rejecting";
      try {
        if (this.rejectTarget.type === "section") {
          await this.$store.dispatch("onboarding/reviewSection", {
            sectionId: this.rejectTarget.item.id,
            decision: "rejected",
            rejectionCategoryId: this.rejectForm.category_id,
            reason: this.rejectForm.reason,
          });
        } else {
          await this.$store.dispatch("onboarding/reviewDocument", {
            documentId: this.rejectTarget.item.id,
            decision: "rejected",
            rejectionCategoryId: this.rejectForm.category_id,
            reason: this.rejectForm.reason,
          });
        }
        this.$bvToast.toast(`${this.rejectTarget.name} rejected.`, { variant: "danger", solid: true });
        this.showRejectModal = false;
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || "Failed to reject.", { variant: "danger", solid: true });
      } finally {
        this.actionInProgress = null;
      }
    },
    viewDocument(file) {
      if (file?.file_url) {
        window.open(file.file_url, "_blank", "noopener,noreferrer");
      }
    },
    async approveDocument(file) {
      if (!this.canReviewSections) {
        if (!this.isAssigned) {
          this.$bvToast.toast("Please assign this submission before approving.", { variant: "warning", solid: true });
        } else if (this.isTier1Approved) {
          this.$bvToast.toast("Tier 1 has been approved. Documents can no longer be modified.", { variant: "warning", solid: true });
        } else {
          this.$bvToast.toast("You do not have permission to review documents.", { variant: "warning", solid: true });
        }
        return;
      }
      this.actionInProgress = `doc-approve-${file.id}`;
      try {
        await this.$store.dispatch("onboarding/reviewDocument", {
          documentId: file.id,
          decision: "approved",
        });
        this.$bvToast.toast(`${file.file_name} approved.`, { variant: "success", solid: true });
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || "Failed to approve document.", { variant: "danger", solid: true });
      } finally {
        this.actionInProgress = null;
      }
    },
    async submitFinalDecision(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (this.isFullyApproved) {
        this.showFinalDecisionModal = false;
        return;
      }
      if (!this.isAssigned) {
        this.$bvToast.toast("Please assign this submission before making a decision.", { variant: "warning", solid: true });
        return;
      }
      if (!this.modalFormValid) {
        this.$bvToast.toast("Please complete all required fields.", { variant: "warning", solid: true });
        return;
      }
      this.actionInProgress = "final-decision";
      try {
        let result;
        const decisionLabels = { approved: "approved", changes_requested: "sent back for changes", rejected: "rejected" };
        if (!this.isTier1Approved) {
          // Tier 1
          const decision = this.finalDecisionForm.decision;
          result = await this.$store.dispatch("onboarding/submitTier1Decision", {
            submissionId: this.submissionId,
            decision,
            reason: decision !== "approved" ? this.finalDecisionForm.reason : undefined,
          });
          this.$bvToast.toast(
            result.message || `Tier 1 decision: ${decisionLabels[decision]}.`,
            { variant: decision === "approved" ? "success" : "warning", solid: true }
          );
        } else {
          // Tier 2
          const decision = this.tier2Form.decision;
          const payload = {
            submissionId: this.submissionId,
            decision,
          };
          if (decision === "approved") {
            payload.approvedProducts = this.tier2Form.approvedProducts;
            payload.approvedCountries = this.tier2Form.approvedCountries;
            const limits = {};
            if (this.tier2Form.volumeLimits.per_transaction) limits.per_transaction = this.tier2Form.volumeLimits.per_transaction;
            if (this.tier2Form.volumeLimits.daily) limits.daily = this.tier2Form.volumeLimits.daily;
            if (this.tier2Form.volumeLimits.monthly) limits.monthly = this.tier2Form.volumeLimits.monthly;
            if (Object.keys(limits).length) payload.volumeLimits = limits;
          } else {
            payload.reason = this.tier2Form.reason;
          }
          result = await this.$store.dispatch("onboarding/submitTier2Decision", payload);
          this.$bvToast.toast(
            result.message || `Tier 2 decision: ${decisionLabels[decision]}.`,
            { variant: decision === "approved" ? "success" : "warning", solid: true }
          );
        }
        this.showFinalDecisionModal = false;
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || "Failed to submit decision.", { variant: "danger", solid: true });
      } finally {
        this.actionInProgress = null;
      }
    },
    resetTier2Form() {
      this.finalDecisionForm = { decision: null, reason: "" };
      this.tier2Form = {
        decision: null,
        reason: "",
        approvedProducts: [],
        approvedCountries: [],
        volumeLimits: { daily: null, monthly: null, per_transaction: null },
        checklist: {
          kyb_kyc_complete: false,
          sanctions_pep_screening: false,
          risk_assessment_recorded: false,
          required_docs_approved: false,
        },
      };
      this.countrySearch = "";
      if (this.isFullyApproved) {
        // Re-fetch submission detail to get approval_tier data
        if (!this.approvalTier) {
          this.$store.dispatch("onboarding/fetchSubmissionDetail", this.submissionId);
        }
      } else if (this.isTier1Approved) {
        // Fetch products and countries from API when opening Tier 2 modal
        this.$store.dispatch("onboarding/fetchAvailableProducts", this.submissionId);
        this.$store.dispatch("onboarding/fetchAvailableCountries", this.submissionId);
      }
    },
    removeCountry(countryId) {
      this.tier2Form.approvedCountries = this.tier2Form.approvedCountries.filter((c) => c !== countryId);
    },
    getCountryName(countryId) {
      const country = this.availableCountries.find((c) => c.id === countryId);
      return country ? country.name : countryId;
    },

    // Document display
    getDocumentName(doc) {
      if (doc?.name) return doc.name;
      if (doc?.document_type_code) {
        return doc.document_type_code
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
      }
      return "Document";
    },

    // Schema-driven field helpers
    formatFieldKey(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    inferFieldType(value) {
      if (typeof value === "boolean") return "boolean";
      if (Array.isArray(value)) return "array";
      if (value && typeof value === "object") return "object";
      return "string";
    },
    isObjectValue(value) {
      return value && typeof value === "object" && !Array.isArray(value);
    },
    isArrayOfObjects(value) {
      return Array.isArray(value) && value.length > 0 && typeof value[0] === "object";
    },
    filterNonNull(obj) {
      if (!obj || typeof obj !== "object") return {};
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== null && value !== undefined && value !== "") {
          result[key] = value;
        }
      }
      return result;
    },
    getNestedLabel(parentSchema, subKey) {
      const nested = parentSchema?.properties?.[subKey];
      if (nested?.title) return nested.title;
      const itemProps = parentSchema?.items?.properties?.[subKey];
      if (itemProps?.title) return itemProps.title;
      return this.formatFieldKey(subKey);
    },
    isReferenceObject(value) {
      return (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        typeof value.name === "string" &&
        value.name.length > 0
      );
    },
    formatReferenceObject(value) {
      if (!value) return "—";
      if (value.code) return `${value.name} (${value.code})`;
      return value.name;
    },
    isDateObject(value) {
      return (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        Number.isInteger(value.day) &&
        Number.isInteger(value.month) &&
        Number.isInteger(value.year)
      );
    },
    formatDateObject(value) {
      if (!value) return "—";
      const d = new Date(Date.UTC(value.year, value.month - 1, value.day));
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    formatDisplayValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (this.isReferenceObject(value)) return this.formatReferenceObject(value);
      if (this.isDateObject(value)) return this.formatDateObject(value);
      if (typeof value === "object" && !Array.isArray(value)) {
        return Object.values(value).filter(Boolean).join(", ");
      }
      if (Array.isArray(value)) return value.join(", ");
      return String(value);
    },

    formatAuditAction(entry) {
      const actionMap = {
        submission_assigned: "Assigned submission",
        section_approved: "Approved section",
        section_rejected: "Rejected section",
        document_approved: "Approved document",
        document_rejected: "Rejected document",
        tier1_decision: `Tier 1 decision: ${entry.new_values?.decision || ""}`,
        tier2_decision: `Tier 2 decision: ${entry.new_values?.decision || ""}`,
      };
      return actionMap[entry.action] || entry.action;
    },

    // Formatting helpers
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    },
    formatFileSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / 1048576).toFixed(1) + " MB";
    },
    formatAddress(addr) {
      if (!addr) return "—";
      if (typeof addr === "string") return addr;
      return [addr.line1, addr.city, addr.state, addr.postal_code, addr.country].filter(Boolean).join(", ");
    },

    // Badge / icon helpers
    statusLabel(status) {
      const map = { draft: "Draft", pending_review: "Pending Review", in_review: "In Review", requires_attention: "Requires Attention", tier1_approved: "Tier 1 Approved", tier2_approved: "Fully Approved", rejected: "Rejected", withdrawn: "Withdrawn" };
      return map[status] || status;
    },
    statusBadgeClass(status) {
      const map = { draft: "badge-default", pending_review: "badge-primary", in_review: "badge-info", requires_attention: "badge-warning", tier1_approved: "badge-info", tier2_approved: "badge-success", rejected: "badge-danger", withdrawn: "badge-default" };
      return map[status] || "badge-secondary";
    },
    tierLabel(status) {
      const map = { pending: "Pending", approved: "Approved", rejected: "Rejected", changes_requested: "Changes Requested" };
      return map[status] || status;
    },
    tierBadgeClass(status) {
      const map = { pending: "badge-default", approved: "badge-success", rejected: "badge-danger", changes_requested: "badge-warning" };
      return map[status] || "badge-secondary";
    },
    sectionIcon(status) {
      const map = { not_started: "○", incomplete: "○", completed: "○", pending_review: "○", approved: "●", rejected: "✕" };
      return map[status] || "○";
    },
    sectionIconClass(status) {
      const map = { approved: "text-success", rejected: "text-danger", pending_review: "text-muted" };
      return map[status] || "text-muted";
    },
    sectionStatusLabel(status) {
      const map = { not_started: "Not Started", incomplete: "Incomplete", completed: "Completed", pending_review: "Pending Review", approved: "Approved", rejected: "Rejected" };
      return map[status] || status;
    },
    sectionStatusBadgeClass(status) {
      const map = { not_started: "badge-default", incomplete: "badge-warning", completed: "badge-primary", pending_review: "badge-primary", approved: "badge-success", rejected: "badge-danger" };
      return map[status] || "badge-secondary";
    },
    docStatusLabel(status) {
      const map = { uploaded: "Uploaded", pending_review: "Pending Review", approved: "Approved", rejected: "Rejected" };
      return map[status] || status;
    },
    docStatusBadgeClass(status) {
      const map = { uploaded: "badge-default", pending_review: "badge-primary", approved: "badge-success", rejected: "badge-danger" };
      return map[status] || "badge-secondary";
    },
  },
};
</script>

<style scoped>
.section-nav-item {
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s;
  margin-bottom: 2px;
}
.section-nav-item:hover {
  background-color: #f0f0f0;
}
.section-nav-item.active {
  background-color: #e8f0fe;
  font-weight: 600;
}
.section-icon {
  display: inline-block;
  width: 20px;
  text-align: center;
}
.bg-warning-light {
  background-color: #fff3cd;
}
.bg-danger-light {
  background-color: #f8d7da;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
